import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCatalogSettings, useTheme } from '../../App';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import './Products.css';
import { getSettings, getCategories } from '../../services/settingsCache';
import { apiClient } from '../../services/apiClient';
import { motion } from 'framer-motion';
import {
  AppWindow,
  PanelLeft,
  DoorOpen,
  DoorClosed,
  Home,
  Building2,
  Sparkles,
  ShieldCheck,
  Brush,
  PaintBucket,
  Waves,
  TreePine,
  Layers,
  LayoutGrid,
  Lock,
  Key,
  Palette,
  Bath,
  Wrench,
  CookingPot,
  Pipette,
  Umbrella,
  Grid,
  Tag
} from 'lucide-react';


const PAGE_SIZE = 12;

function formatCurrency(price) {
  if (typeof price !== 'number' || Number.isNaN(price)) return '';
  return `Rs. ${price.toFixed(2)}`;
}

function toOptions(items) {
  return Array.isArray(items) ? items : [];
}


function getCategoryIcon(name, slug, size = 15) {
  const s = `${name || ''} ${slug || ''}`.toLowerCase();
  const iconProps = { size, style: { flexShrink: 0, strokeWidth: 1.8 } };

  // Windows & Doors
  if (s.includes('window')) return <AppWindow {...iconProps} />;
  if (s.includes('sliding')) return <PanelLeft {...iconProps} />;
  if (s.includes('door') && (s.includes('window') || s.includes('&'))) return <DoorOpen {...iconProps} />;
  if (s.includes('door')) return <DoorClosed {...iconProps} />;

  // Paints & finishes
  if (s.includes('interior')) return <Home {...iconProps} />;
  if (s.includes('exterior')) return <Building2 {...iconProps} />;
  if (s.includes('enamel')) return <Sparkles {...iconProps} />;
  if (s.includes('primer')) return <ShieldCheck {...iconProps} />;
  if (s.includes('distemper')) return <Brush {...iconProps} />;
  if (s.includes('paint')) return <PaintBucket {...iconProps} />;

  // Plywood & Wood
  if (s.includes('marine')) return <Waves {...iconProps} />;
  if (s.includes('commercial')) return <Building2 {...iconProps} />;
  if (s.includes('veneer')) return <TreePine {...iconProps} />;
  if (s.includes('plywood') || s.includes('ply') || s.includes('wood')) return <Layers {...iconProps} />;

  // Laminate
  if (s.includes('laminate')) return <LayoutGrid {...iconProps} />;

  // Hardware, Locks & Fittings
  if (s.includes('lock') || s.includes('fitting') || s.includes('hardware')) return <Lock {...iconProps} />;
  if (s.includes('key')) return <Key {...iconProps} />;

  // Decor
  if (s.includes('decor')) return <Palette {...iconProps} />;

  // Sanitary & Plumbing
  if (s.includes('sanitary') || s.includes('bath')) return <Bath {...iconProps} />;
  if (s.includes('plumb') || s.includes('pipe') || s.includes('tap') || s.includes('valve')) return <Wrench {...iconProps} />;

  // Kitchen
  if (s.includes('kitchen') || s.includes('appliance') || s.includes('cook')) return <CookingPot {...iconProps} />;

  // Adhesive & Waterproofing
  if (s.includes('adhesive') || s.includes('glue') || s.includes('fevicol')) return <Pipette {...iconProps} />;
  if (s.includes('waterproof')) return <Umbrella {...iconProps} />;

  // All / General
  if (s.includes('all')) return <Grid {...iconProps} />;

  // Fallback
  return <Tag {...iconProps} />;
}

export default function Products() {
  const { isNightMode } = useTheme();
  const { showProductRate } = useCatalogSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [subcategories, setSubcategories] = useState({});
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [enquiryStatus, setEnquiryStatus] = useState('');
  const [heroBadge, setHeroBadge] = useState('Our Products');
  const [heroTitle, setHeroTitle] = useState('Product Catalog');
  const [heroSubtitle, setHeroSubtitle] = useState(
    'Browse our extensive collection of premium construction materials.',
  );
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [ctaBadge, setCtaBadge] = useState('Special Offer');
  const [ctaTitle, setCtaTitle] = useState('Need Help Choosing?');
  const [ctaSubtitle, setCtaSubtitle] = useState(
    'Our expert team is here to help you select the right materials for your project. Get free consultation and personalized recommendations.',
  );
  const [ctaButtonText, setCtaButtonText] = useState('Contact Our Experts');
  const [ctaImageUrl, setCtaImageUrl] = useState('');
  const [maxPrice, setMaxPrice] = useState(0);
  const [globalMaxPrice, setGlobalMaxPrice] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const selectedCategory = searchParams.get('category') || 'all';
  const selectedBrand = searchParams.get('brand') || 'all';
  const selectedSort = searchParams.get('sort') || 'latest';
  const selectedSearch = searchParams.get('q') || '';
  const selectedAvailability = searchParams.get('availability') || 'all';
  const currentPage = Math.max(parseInt(searchParams.get('page') || '1', 10), 1);
  const maxPriceParam = searchParams.get('maxPrice');
  const hasMaxPriceFilter = searchParams.has('maxPrice');
  const selectedMaxPrice = hasMaxPriceFilter ? Number(maxPriceParam || '0') : globalMaxPrice;

  const mainCategories = useMemo(
    () => categories.filter(cat => !cat.parentCategory),
    [categories],
  );

  const [searchVal, setSearchVal] = useState(selectedSearch);

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);

    if (!value || value === 'all' || value === 'latest' || (key === 'maxPrice' && Number(value) >= Math.ceil(globalMaxPrice))) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }

    next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams, globalMaxPrice]);

  useEffect(() => {
    setSearchVal(selectedSearch);
  }, [selectedSearch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const trimmed = searchVal.trim();
      if (trimmed !== selectedSearch) {
        setParam('q', trimmed);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchVal, selectedSearch, setParam]);

  const loadSubcategories = useCallback(async (categorySlug) => {
    try {
      // Get subcategories from public API endpoint
      const subcategoriesResponse = await apiClient.get(`/categories/${categorySlug}/subcategories`);
      
      // Handle the response format from backend {success: true, data: subcategories}
      const subcategoriesData = subcategoriesResponse?.success && subcategoriesResponse?.data 
        ? subcategoriesResponse.data 
        : (Array.isArray(subcategoriesResponse) ? subcategoriesResponse : []);
      
      setSubcategories(prev => ({
        ...prev,
        [categorySlug]: subcategoriesData
      }));
    } catch (err) {
      console.error('Failed to load subcategories:', err);
      // Set empty array on error to prevent repeated attempts
      setSubcategories(prev => ({
        ...prev,
        [categorySlug]: []
      }));
    }
  }, []);

  const toggleCategoryExpansion = useCallback((cat) => {
    const categoryKey = cat.slug;
    
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
        // Load subcategories if not already loaded
        if (!subcategories[categoryKey]) {
          loadSubcategories(categoryKey);
        }
      }
      return newSet;
    });
  }, [subcategories, loadSubcategories]);

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async (retryCount = 0) => {
      try {
        setFiltersLoading(true);
        const categoriesResponse = await getCategories();

        if (cancelled) return;

        // Handle category response format {success: true, data: categories}
        const categoriesData = categoriesResponse?.success && categoriesResponse?.data 
          ? categoriesResponse.data 
          : (Array.isArray(categoriesResponse) ? categoriesResponse : []);
        
        setCategories(toOptions(categoriesData));
      } catch (err) {
        console.error('Failed to load category/brand filters', err);
        
        // Retry logic
        if (retryCount < 2 && !cancelled) {
          console.log(`Retrying categories load... Attempt ${retryCount + 1}`);
          setTimeout(() => loadFilters(retryCount + 1), 1000 * (retryCount + 1));
        } else {
          // Set empty categories on error to prevent infinite loading
          if (!cancelled) {
            setCategories([]);
          }
        }
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    if (!Array.isArray(mainCategories) || mainCategories.length === 0) return;

    const isValid = mainCategories.some((cat) => String(cat.slug) === String(selectedCategory));
    if (!isValid) {
      setParam('category', 'all');
    }
  }, [mainCategories, selectedCategory, setParam]);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError('');

        const query = new URLSearchParams({
          format: 'paginated',
          page: String(currentPage),
          limit: String(PAGE_SIZE),
          sort: selectedSort,
        });

        if (selectedCategory !== 'all') query.set('categorySlug', selectedCategory);
        if (selectedBrand !== 'all') query.set('brand', selectedBrand);
        if (selectedSearch.trim()) query.set('q', selectedSearch.trim());
        if (selectedAvailability === 'in-stock') query.set('inStock', 'true');
        else if (selectedAvailability === 'out-of-stock') query.set('inStock', 'false');
        else query.delete('inStock');
        if (hasMaxPriceFilter) query.set('maxPrice', String(selectedMaxPrice));

        const data = await apiClient.get(`/products?${query.toString()}`);

        if (cancelled) return;

        const items = Array.isArray(data?.items) ? data.items : [];
        const prices = items
          .map((item) => item.price)
          .filter((value) => typeof value === 'number' && !Number.isNaN(value));

        setProducts(items);
        setTotalProducts(typeof data?.total === 'number' ? data.total : items.length);
        setTotalPages(Math.max(data?.totalPages || 1, 1));
        
        const pageMax = prices.length ? Math.max(...prices) : 0;
        setMaxPrice(pageMax);
        if (pageMax > globalMaxPrice) {
          setGlobalMaxPrice(pageMax);
        }
      } catch (err) {
        console.error('Failed to load products from backend', err);
        if (!cancelled) {
          setError(err.message || 'Unable to load products from server.');
          setProducts([]);
          setTotalProducts(0);
          setTotalPages(1);
          setMaxPrice(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    selectedAvailability,
    selectedBrand,
    selectedCategory,
    maxPriceParam,
    selectedSearch,
    selectedSort,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (!cancelled && data) {
          setHeroBadge(data.productsHeroBadge || 'Our Products');
          setHeroTitle(data.productsHeroTitle || 'Product Catalog');
          setHeroSubtitle(
            data.productsHeroSubtitle ||
              'Browse our extensive collection of premium construction materials.',
          );
          setHeroImageUrl(data.productsHeroImageUrl || '');
          setCtaBadge(data.productsCtaBadge || 'Special Offer');
          setCtaTitle(data.productsCtaTitle || 'Need Help Choosing?');
          setCtaSubtitle(
            data.productsCtaSubtitle ||
              'Our expert team is here to help you select the right materials for your project. Get free consultation and personalized recommendations.',
          );
          setCtaButtonText(data.productsCtaButtonText || 'Contact Our Experts');
          setCtaImageUrl(data.productsCtaImageUrl || '');
        }
      } catch (err) {
        console.error('Failed to load settings', err);
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    }
  }, []);

const handleSubmitEnquiry = async () => {
  // Validation
  if (!enquiryData.name.trim() || !enquiryData.phone.trim() || !enquiryData.message.trim()) {
    setEnquiryStatus('Please fill in all required fields (Name, Phone, and Message)');
    setTimeout(() => setEnquiryStatus(''), 3000);
    return;
  }

  // Store data before clearing
  const originalEnquiryData = { ...enquiryData };

  // INSTANT SUCCESS - Show success immediately
  setEnquiryData({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  setEnquiryStatus('Enquiry submitted successfully! We will contact you soon.');
  
  // Close modal after 1 second and clear status
  setTimeout(() => {
    setShowEnquiryModal(false);
    setEnquiryStatus('');
  }, 1000);

  // Process in background WITHOUT blocking UI
  (async () => {
    try {
      await apiClient.post('/contact', {
        name: originalEnquiryData.name,
        email: originalEnquiryData.email,
        phone: originalEnquiryData.phone,
        subject: 'Enquiry from Products Page',
        message: originalEnquiryData.message
      });
      // Success - no action needed as we already showed success
    } catch (error) {
      console.error('Background submission failed:', error);
      // If background fails, show error and restore form
      setEnquiryData(originalEnquiryData);
      setEnquiryStatus('Failed to submit enquiry. Please try again or contact us directly.');
      setTimeout(() => setEnquiryStatus(''), 5000);
    }
  })();
};

const updatePage = (page) => {
  const next = new URLSearchParams(searchParams);
  if (page <= 1) next.delete('page');
  else next.set('page', String(page));
  setSearchParams(next);
};

  return (
    <main
      style={{
        background: '#ffffff',
        color: isNightMode ? '#111827' : '#111827',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <motion.section 
        className="products-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="products-hero__media">
          <img
            src={heroImageUrl || `${process.env.PUBLIC_URL}/assets/images/Container.png`}
            alt=""
            className="products-hero__image"
            decoding="async"
            fetchpriority="high"
          />
          <div aria-hidden="true" className="products-hero__overlay" />
          <motion.div 
            className="products-hero__content"
            style={{
              position: 'absolute',
              zIndex: 5,
              top: 0,
              left: 0,
              right: 0,
              paddingLeft: '40px',
              paddingRight: '40px',
              transform: 'none',
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
              <motion.div 
                className="products-hero__pill"
                style={{ textAlign: 'left' }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              >
                {heroBadge}
              </motion.div>
              <motion.h1 
                className="products-hero__title"
                style={{ textAlign: 'left' }}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {heroTitle}
              </motion.h1>
              <motion.p 
                className="products-hero__subtitle"
                style={{ textAlign: 'left' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 0.9, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                {heroSubtitle}
              </motion.p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      <div
        style={{
          background: isNightMode ? '#1e293b' : '#F8F9FA',
          padding: '15px 40px',
          fontSize: '0.95rem',
          color: isNightMode ? '#cbd5e1' : '#6c757d',
          borderBottom: isNightMode ? '1px solid #334155' : '1px solid #e5e5e5',
        }}
      >
        <NavLink
          to="/"
          style={{
            textDecoration: 'none',
            color: isNightMode ? '#cbd5e1' : '#6c757d',
            fontWeight: 500,
            marginRight: 4,
          }}
        > Home
            </NavLink>
        <span aria-hidden>{'>'}</span>{' '}
        <span style={{ color: isNightMode ? '#fff' : '#000', marginLeft: 4 }}>Catalog</span>
      </div>

      <motion.section 
        aria-label="Products" 
        className="products-breadcrumb products-layout"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="products-layout__inner">
          <motion.div 
            className="products-filters-card" 
            aria-label="Products filters summary"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="products-filters-card__top">
              <span className="products-filters-count">{totalProducts} products</span>
            </div>
            <div className="products-filters-card__bottom" style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <input
                type="search"
                value={searchVal}
                onChange={(event) => setSearchVal(event.target.value)}
                placeholder="Search products"
                className="products-filters-select"
                aria-label="Search products"
                style={{ maxWidth: 220 }}
              />
              </div>
            
            {/* Mobile Categories List */}
            <div className="mobile-categories-list" style={{ marginTop: 12 }}>
              <select
                className="products-filters-select"
                value={selectedCategory}
                onChange={(event) => {
                  const value = event.target.value;
                  setParam('category', value);
                  // Always load subcategories when a category is selected
                  if (value !== 'all') {
                    loadSubcategories(value);
                  }
                }}
                aria-label="Filter by category"
                style={{
                  maxHeight: '200px',
                }}
              >
                <option value="all">All Categories</option>
                {mainCategories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
              
              {/* Subcategories display */}
              {selectedCategory !== 'all' && subcategories[selectedCategory] && (
                <div style={{ 
                  marginTop: 8,
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  backgroundColor: '#f9fafb',
                  padding: 8
                }}>
                  <div style={{ display: 'grid', gap: 6, maxHeight: '150px', overflowY: 'auto' }}>
                    {subcategories[selectedCategory].map((subcat) => (
                      <button
                                key={subcat.slug || subcat._id}
                                type="button"
                                onClick={() => navigate(`/products/${subcat.slug || subcat._id}`)}
                                style={{
                                  textAlign: 'left',
                                  width: '100%',
                                  padding: '7px 16px',
                                  border: 'none',
                                  background: selectedCategory === (subcat.slug || subcat._id) ? '#e8f5e8' : 'transparent',
                                  color: '#111827',
                                  fontSize: '13px',
                                  fontWeight: selectedCategory === (subcat.slug || subcat._id) ? 600 : 400,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                              >
                                {getCategoryIcon(subcat.name, subcat.slug, 14)}
                                <span>{subcat.name}</span>
                              </button>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedCategory !== 'all' && !subcategories[selectedCategory] && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280',
                  textAlign: 'center',
                  marginTop: '8px',
                  padding: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  backgroundColor: '#f9fafb'
                }}>
                  Loading subcategories...
                </div>
              )}
              
              {mainCategories.length === 0 && filtersLoading && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280',
                  textAlign: 'center',
                  marginTop: '4px'
                }}>
                  Loading categories...
                </div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <select
                className="products-filters-select"
                value={selectedSort}
                aria-label="Sort products"
                onChange={(event) => setParam('sort', event.target.value)}
              >
                <option value="latest">Latest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name_asc">Name: A to Z</option>
                <option value="name_desc">Name: Z to A</option>
              </select>
            </div>
          </motion.div>

          {error && (
            <div style={{ marginTop: 10, color: '#dc2626', fontSize: 13 }}>{error}</div>
          )}

          <div className="products-layout__grid">
            <aside
              aria-label="Categories sidebar"
              className="products-layout__sidebar"
              style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}
            >
              <div style={{ fontWeight: 600, color: '#111827', margin: '2px 0 10px', fontFamily: 'Arial, sans-serif' }}>
                Categories
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setParam('category', 'all')}
                  style={{
                    textAlign: 'left',
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    background: selectedCategory === 'all' ? '#a7e08d' : '#ffffff',
                    color: '#111827',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  All Categories
                </button>
                {mainCategories.map((cat) => (
                  <div key={cat.slug} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', width: '100%' }}>
                      <button
                        type="button"
                        onClick={() => setParam('category', cat.slug)}
                        style={{
                          textAlign: 'left',
                          flex: 1,
                          padding: '8px 10px',
                          border: 'none',
                          background: selectedCategory === cat.slug ? '#a7e08d' : '#ffffff',
                          color: '#111827',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {getCategoryIcon(cat.name, cat.slug, 16)}
                        <span>{cat.name}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategoryExpansion(cat);
                        }}
                        style={{
                          padding: '8px 10px',
                          border: 'none',
                          background: selectedCategory === cat.slug ? '#a7e08d' : '#ffffff',
                          color: '#6b7280',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'transform 0.2s ease',
                          transform: expandedCategories.has(cat.slug) ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}
                      >
                        {String.fromCharCode(9654)}
                      </button>
                    </div>
                    {expandedCategories.has(cat.slug) && (
                      <div style={{ 
                        background: '#f8f9fa', 
                        borderTop: '1px solid #e5e7eb',
                        padding: '4px 0',
                        borderLeft: '3px solid #a7e08d',
                        marginLeft: '20px',
                        textAlign: 'left'
                      }}>
                        {subcategories[cat.slug] ? (
                          subcategories[cat.slug].length > 0 ? (
                            subcategories[cat.slug].map((subcat) => (
                              <button
                                key={subcat.slug || subcat._id}
                                type="button"
                                onClick={() => navigate(`/products/${subcat.slug || subcat._id}`)}
                                style={{
                                  textAlign: 'left',
                                  width: '100%',
                                  padding: '8px 16px',
                                  border: 'none',
                                  background: selectedCategory === (subcat.slug || subcat._id) ? '#e8f5e8' : 'transparent',
                                  color: '#111827',
                                  fontSize: '14px',
                                  fontWeight: selectedCategory === (subcat.slug || subcat._id) ? 600 : 400,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px'
                                }}
                              >
                                {getCategoryIcon(subcat.name, subcat.slug, 15)}
                                <span>{subcat.name}</span>
                              </button>
                            ))
                          ) : (
                            <div style={{ padding: '6px 20px', fontSize: '12px', color: '#6b7280', textAlign: 'left' }}>
                              No subcategories available
                            </div>
                          )
                        ) : (
                          <div style={{ padding: '6px 20px', fontSize: '12px', color: '#6b7280', textAlign: 'left' }}>
                            Loading subcategories...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {mainCategories.length === 0 && filtersLoading && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Loading categories...</div>
                )}
              </div>

              <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '14px 0' }} />

              {/* Brands section commented out
              <div style={{ fontWeight: 600, color: '#111827', margin: '2px 0 10px', fontFamily: 'Arial, sans-serif' }}>
                Brands
              </div>
              <select
                className="products-filters-select"
                value={selectedBrand}
                onChange={(event) => setParam('brand', event.target.value)}
                aria-label="Filter by brand"
                style={{ width: '100%' }}
              >
                <option value="all">All Brands</option>
                {allBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              */}

              <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '14px 0' }} />

              <div style={{ fontWeight: 600, color: '#111827', margin: '2px 0 10px', fontFamily: 'Arial, sans-serif' }}>
                Price Range
              </div>
              {globalMaxPrice > 0 ? (
                <>
                  <input
                    type="range"
                    min={0}
                    max={Math.ceil(globalMaxPrice)}
                    value={selectedMaxPrice}
                    onChange={(event) => setParam('maxPrice', event.target.value)}
                    style={{ width: '100%', marginTop: 8 }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: '#6b7280',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 4,
                    }}
                  >
                    <span>Rs. 0</span>
                    <span>Rs. {Math.round(selectedMaxPrice)}</span>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Prices will appear here when available.
                </div>
              )}

              <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '14px 0' }} />

              <div style={{ fontWeight: 600, color: '#111827', margin: '2px 0 10px', fontFamily: 'Arial, sans-serif' }}>
                Availability
              </div>
              <select
                className="products-filters-select"
                value={selectedAvailability}
                onChange={(event) => setParam('availability', event.target.value)}
                aria-label="Filter by stock"
                style={{ width: '100%' }}
              >
                <option value="all">All</option>
                <option value="in-stock">In Stock</option>
                <option value="out-of-stock">Out of Stock</option>
              </select>
            </aside>

            {!isLoading && products.length === 0 && (
              <div style={{ padding: 16, fontFamily: 'Arial, sans-serif', color: '#6b7280' }}>
                No products found for the current filters.
              </div>
            )}

            {(isLoading || products.length > 0) && (
              <div>
                <div className="products-layout__cards">
                  {isLoading
                    ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                        <div
                          key={`skeleton-${index}`}
                          style={{
                            borderRadius: 12,
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            background: '#ffffff',
                          }}
                        >
                          <div style={{ width: '100%', aspectRatio: '4/3', background: '#e5e7eb' }} />
                          <div style={{ padding: 12, display: 'grid', gap: 8 }}>
                            <div style={{ height: 14, width: '72%', background: '#e5e7eb' }} />
                            <div style={{ height: 12, width: '42%', background: '#e5e7eb' }} />
                          </div>
                        </div>
                      ))
                    : products.map((product, index) => (
                        <motion.article
                          key={product._id}
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/product/${product._id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              navigate(`/product/${product._id}`);
                            }
                          }}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            overflow: 'hidden',
                            background: '#fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            cursor: 'pointer',
                          }}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-100px" }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          whileHover={{ scale: 1.03, y: -5 }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '4/3',
                              overflow: 'hidden',
                              backgroundColor: '#f3f4f6',
                              position: 'relative',
                            }}
                          >
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                loading="lazy"
                                decoding="async"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                <Layers size={36} strokeWidth={1.5} />
                              </div>
                            )}
                          </div>
                          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', flex: 1, gap: 6 }}>
                            <h3 style={{ margin: 0, fontSize: 16, color: '#111827', fontFamily: 'Arial, sans-serif' }}>
                              {product.name}
                            </h3>
                            {product.brand ? (
                              <div style={{ fontSize: 12, color: '#6b7280', minHeight: 18 }}>{product.brand}</div>
                            ) : (
                              <div style={{ fontSize: 12, color: 'transparent', minHeight: 18, userSelect: 'none' }}>&nbsp;</div>
                            )}
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 11,
                                color: product.inStock !== false ? '#15803d' : '#dc2626',
                              }}
                            >
                              {product.inStock !== false ? 'In stock' : 'Out of stock'}
                            </div>
                            <div style={{ marginTop: 2, color: '#16a34a', fontWeight: 600, minHeight: 24 }}>
                              {showProductRate && typeof product.price === 'number'
                                ? formatCurrency(product.price)
                                : 'Contact for Price'}
                            </div>
                            <div style={{ marginTop: 'auto', paddingTop: 8, width: '100%' }}>
                              <NavLink
                                to={`/product/${product._id}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  padding: '9px 12px',
                                  borderRadius: 8,
                                  border: '1px solid #5d7f4e',
                                  background: 'transparent',
                                  color: '#166534',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  boxSizing: 'border-box',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {showProductRate ? 'View Product' : 'Get Best Price'}
                              </NavLink>
                            </div>
                          </div>
                        </motion.article>
                      ))}
                </div>

                {totalPages > 1 && !isLoading && (
                  <motion.div 
                    style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap' }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <button
                      type="button"
                      onClick={() => updatePage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}
                    >
                      Previous
                    </button>
                    <span style={{ alignSelf: 'center', color: '#6b7280' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => updatePage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}
                    >
                      Next
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.section>

      <motion.section 
        aria-labelledby="products-cta-title" 
        className="products-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="products-cta__inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="products-cta__grid">
            <motion.div 
              className="products-cta__content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div 
                className="products-cta__pill"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                {ctaBadge}
              </motion.div>
              <motion.h2 
                id="products-cta-title" 
                className="products-cta__title"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {ctaTitle}
              </motion.h2>
              <motion.p 
                className="products-cta__text"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {ctaSubtitle}
              </motion.p>
              <motion.button 
                onClick={() => setShowEnquiryModal(true)}
                className="products-cta__button"
                style={{
                  background: '#16a34a',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  textDecoration: 'none',
                  fontSize: '16px'
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                {ctaButtonText}
              </motion.button>
            </motion.div>
            <motion.div 
              className="products-cta__media"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.02 }}
            >
              <img
                src={ctaImageUrl || `${process.env.PUBLIC_URL}/assets/images/w1.png`}
                alt="Consultation Room"
                loading="lazy"
                decoding="async"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowEnquiryModal(false)}
        >
          <div
            style={{
              backgroundColor: isNightMode ? '#1e293b' : '#fff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowEnquiryModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: isNightMode ? '#cbd5e1' : '#666'
              }}
            >
              ×
            </button>

            {/* Modal Header */}
            <h2 style={{ 
              marginBottom: '20px', 
              color: isNightMode ? '#f1f5f9' : '#1a1a1a',
              fontSize: '20px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Contact Us
            </h2>

            {/* Status Message */}
            {enquiryStatus && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '500',
                background: enquiryStatus.includes('successfully') ? '#d4edda' : 
                           enquiryStatus.includes('Submitting') ? '#d1ecf1' : '#f8d7da',
                color: enquiryStatus.includes('successfully') ? '#155724' : 
                       enquiryStatus.includes('Submitting') ? '#0c5460' : '#721c24',
                border: `1px solid ${
                  enquiryStatus.includes('successfully') ? '#c3e6cb' : 
                  enquiryStatus.includes('Submitting') ? '#bee5eb' : '#f5c6cb'
                }`
              }}>
                {enquiryStatus}
              </div>
            )}

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={enquiryData.name}
                  onChange={(e) => setEnquiryData({...enquiryData, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={enquiryData.email}
                  onChange={(e) => setEnquiryData({...enquiryData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={enquiryData.phone}
                  onChange={(e) => setEnquiryData({...enquiryData, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Message *
                </label>
                <textarea
                  value={enquiryData.message}
                  onChange={(e) => setEnquiryData({...enquiryData, message: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333',
                    minHeight: '100px',
                    resize: 'vertical'
                  }}
                  placeholder="Tell us about your requirements..."
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                className="no-popup-submit"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmitEnquiry();
                  return false;
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  width: '100%'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#15803d'}
                onMouseOut={(e) => e.currentTarget.style.background = '#16a34a'}
              >
                Submit Enquiry
              </button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}
